import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { client } from '../../networking/client';
import { RootState } from '../store';

export interface ISalesOrderProduct {
  sales_order_id: number;
  sales_order_external_id: number;
  customer_person_id: number;
  datetime_create: Date;
  person_firstname: string;
  person_lastname: string;
  person_gender_id: number;
  person_birthdate: Date;
  person_email: string;
  record_state: string;
  person_mobile_phone: string;
  person_nationality_id: number;
  business_person_id: number;
  items_subtotal: number;
  amount: number;
  amount_paid?: number;
  vat: number;
  currency: string;
  payment_method_id: number;
  person_id_create: number;
  ta_id: number;
  transaction_id: number;
  payment_method?: string;
  paid?: string;
  product_id?: number;
  product_name?: string;
  unit_of_measure_id?: number;
  quantity?: string;
  voucher_amount?: number;
  start: Date;
  appointment_date?: Date;
  end_time?: Date;
  sales_order_item_id?: string;
  external_sales_order_item_id?: number;
  price?: number;
  item_record_state: string;
}

export interface ISalesOrderProductsState {
  status: string;
  addingStatus: string;
  editingStatus: string;
  salesOrderProducts: ISalesOrderProduct[];
}

const initialState: ISalesOrderProductsState = {
  status: 'idle',
  addingStatus: 'idle',
  editingStatus: 'idle',
  salesOrderProducts: [],
};

export const fetchSalesOrderProducts = createAsyncThunk(
  'salesOrderProducts/fetchAll',
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const salesOrderProductsResponse: any = await client.get(
        `/healthpoint/sales-order-products/${state?.auth?.accountInfo?.account_number}`
      );
      return salesOrderProductsResponse;
    } catch (err: any) {
      return rejectWithValue({});
    }
  }
);

type AddSalesOrderProductReqBody = {
  customerEmail: string;
  businessPersonId: number;
  itemsSubtotal?: number;
  amount?: number;
  vat?: number;
  currency?: string;
  paymentMethodId?: number;
  personIdCreate?: number;
  taId?: number;
  transactionId?: number;
  productId?: number;
  unitOfMeasureId?: number;
  quantity?: string;
};

export const addSalesOrderProduct = createAsyncThunk(
  'salesOrderProduct/add',
  async (reqBody: AddSalesOrderProductReqBody, { dispatch, rejectWithValue }) => {
    try {
      await client.post('/healthpoint/sales-order-product', reqBody);
      dispatch(fetchSalesOrderProducts());
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const salesOrderProductsSlice = createSlice({
  name: 'salesOrderProducts',
  initialState,
  reducers: {
    resetSalesOrderProducts: (_) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesOrderProducts.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchSalesOrderProducts.fulfilled, (state, action) => {
        state.salesOrderProducts = action.payload.data.salesOrderProducts;
        state.status = 'idle';
      })
      .addCase(fetchSalesOrderProducts.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(addSalesOrderProduct.pending, (state) => {
        state.addingStatus = 'pending';
      })
      .addCase(addSalesOrderProduct.fulfilled, (state) => {
        state.addingStatus = 'fulfilled';
      })
      .addCase(addSalesOrderProduct.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Something went wrong.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.addingStatus = 'rejected';
      });
  },
});

export const { resetSalesOrderProducts } = salesOrderProductsSlice.actions;

export default salesOrderProductsSlice.reducer;

export const selectSalesOrderProducts = (state: RootState) =>
  state.salesOrderProducts.salesOrderProducts;
export const selectSalesOrderProductsEditingStatus = (state: RootState) =>
  state.salesOrderProducts.editingStatus;
